import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

type ToastVariant = "success" | "error" | "info";

interface ToastMsg {
  id: string;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  show: (msg: Omit<ToastMsg, "id">) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

const AUTO_DISMISS_MS = 3500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue["show"]>(
    (msg) => {
      const id = crypto.randomUUID();
      setToasts((list) => [...list, { ...msg, id }]);
      setTimeout(() => remove(id), AUTO_DISMISS_MS);
    },
    [remove],
  );

  const value: ToastContextValue = {
    show,
    success: (message) => show({ variant: "success", message }),
    error: (message) => show({ variant: "error", message }),
    info: (message) => show({ variant: "info", message }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastMsg[];
  onDismiss: (id: string) => void;
}) {
  return (
    // top-16 (et non top-4) : le header V2 fait h-14 — à top-4 le toast se
    // superposait au sélecteur de langue et au menu utilisateur, rendant les
    // deux illisibles (retour PO 2026-08-07).
    <div
      className="fixed top-16 right-4 z-[1000] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastMsg; onDismiss: () => void }) {
  const Icon =
    toast.variant === "success" ? CheckCircle2 : toast.variant === "error" ? AlertCircle : Info;

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex items-start gap-2 min-w-[260px] max-w-[420px]",
        "px-3 py-2.5 rounded-md shadow-lg border text-sm",
        // FOND OPAQUE, comme la Modal (bg-surface) : le toast flotte au-dessus
        // d'un contenu quelconque. Les fonds teintés à 10 % (bg-danger/10) sont
        // réservés aux bandeaux et badges POSÉS sur une surface déjà opaque —
        // utilisés ici, l'en-tête transparaissait à travers le message et le
        // rendait illisible (retour PO 2026-08-07). La sémantique passe par la
        // bordure et l'icône ; le texte reste en text-primary pour le contraste.
        "bg-surface text-text-primary",
        "animate-[slideInRight_0.2s_ease-out]",
        toast.variant === "success" && "border-success/40",
        toast.variant === "error" && "border-danger/40",
        toast.variant === "info" && "border-border",
      )}
    >
      <Icon
        size={16}
        className={cn(
          "shrink-0 mt-0.5",
          toast.variant === "success" && "text-success",
          toast.variant === "error" && "text-danger",
          toast.variant === "info" && "text-text-muted",
        )}
      />
      <span className="flex-1 leading-snug">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 opacity-60 hover:opacity-100 cursor-pointer outline-none"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}
